import React, { Suspense, lazy, useEffect, useState } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import "./App.css";
import "../Utils/translator/i18n.js";
import "./chart.js";

// Common Components
import Error from "../Components/Common/Error.js";
import Offline from "../Components/Common/Offline.js";
import Fallback from "../Components/Common/Fallback.js";
import StaffMyProfile from "../Components/Common/StaffMyProfile.js";
import ProtectRoute from "../Routes/ProtectedRoutes/ProtectedRoute";
import { useFirebaseMessaging } from "../Hooks/NotificationHooks/NotificationHooks.js";

// Login Pages and Auth Related
import Home from "../Modules/HomePage/Home";
import ResetPassword from "../Modules/LoginPages/Student/ResetPassword/ResetPassword.js";
import StaffLogin from "../Modules/LoginPages/Staff/StaffLogin.js";
import ForgetPassword from "../Modules/LoginPages/Student/Login/ForgetPassword/ForgetPassword.js";

// Policy Pages
import PrivacyPolicy from "../Modules/LoginPages/Policys/PrivacyPolicy.jsx";
import TermsAndConditions from "../Modules/LoginPages/Policys/TermsAndConditions.jsx";
import CookiePolicy from "../Modules/LoginPages/Policys/CookiePolicy.jsx";

// Admin Profile Management
import AllStaff from "../Modules/Admin/UsersProfiles/StaffProfile/AllStaff.js";

// Finance Modules (Non-lazy)
import ReceiptsMain from "../Modules/Admin/Finance/Reciepts/ReceiptsMain.js";
import RecentReceiptsList from "../Modules/Admin/Finance/Reciepts/RecentReceiptsList.js";
import QuotationMain from "../Modules/Admin/Finance/Quotations/QuotationMain.js";
import CreateReceipt from "../Modules/Admin/Finance/Reciepts/AddReceipt/CreateReceipt.js";
import CreateQuotation from "../Modules/Admin/Finance/Quotations/AddQuotation/CreateQuotation.js";
import InventoryList from "../Modules/Admin/Finance/Inventory/InventoryList.js";
import PayrollManagement from "../Modules/Admin/Finance/PayrollManagement/PayrollManagement.jsx";
import AddPayrollManagement from "../Modules/Admin/Finance/PayrollManagement/AddPayrollManagement.jsx";

// RBAC (Role-Based Access Control)
import ManageRolePage from "../Components/Common/RBAC/ManageRolePage.js";
import AllHelper from "../Modules/Admin/UsersProfiles/HelperProfile/AllHelper.js";
import AllDriver from "../Modules/Admin/UsersProfiles/DriverProfile/AllDriver.js";
import TransportSimulator from "../Components/Common/TransportSimulator.js";
import StaffAttendance from "../Modules/Admin/StaffAttendance/StaffAttendance.jsx";
import IndividualStaffAttendance from "../Modules/Admin/StaffAttendance/IndividualAttendance/MainSection.jsx";
import TakeStaffAttendance from "../Modules/Admin/StaffAttendance/TakeAttendance/TakeStaffAttendance.js";
import BenifitMain from "../Modules/Admin/benifits&compensation/BenifitMain.jsx";
import AddBenifit from "../Modules/Admin/benifits&compensation/AddBenifit.jsx";
import ManagerApproval from "../Modules/Admin/benifits&compensation/ManagerApproval.jsx";
import VisitorMain from "../Modules/Admin/visitorManagement/VisitorMain.jsx";
import AddPreVisitor from "../Modules/Admin/visitorManagement/AddPreVisitor.jsx";
import DirectVisitor from "../Modules/Admin/visitorManagement/DirectVisitor.jsx";
import Main from "../Modules/Admin/careers/Main.jsx";
import DynamicForm from "../Modules/Admin/careers/DynamicForm.jsx";
import CreateJob from "../Modules/Admin/careers/CreateJob.jsx";
import JobApplicationsPage from "../Modules/Admin/jobApplication/JobApplicationsPage.jsx";
import Interviews from "../Modules/Admin/interviews/Interviews.js"
import ScheduleInterviewMain from "../Modules/Admin/jobApplication/interview/Main.jsx";
import OverviewMain from "../Modules/Admin/Finance/overview/OverviewMain.jsx";
import InvestmentMain from "../Modules/Admin/Finance/investment/InvestmentMain.jsx";
import LiabilitiesMain from "../Modules/Admin/Finance/liabilities/LiabilitiesMain.jsx";
import FinancialStatements from "../Modules/Admin/Finance/financalStatements/FinancialStatements.js";
import FixedAssets from "../Modules/Admin/Finance/fixedAssets/FixedAssets.js";
import CashFlow from "../Modules/Admin/Finance/cashFlow/CashFlow.js";


const IntegrateCarrersDocs = lazy(
  () => import("../Modules/Admin/careers/IntegrateCarrersDocs.jsx")
);

const UserProfile = lazy(
  () => import("../Modules/Admin/UsersProfiles/AdminProfile/UserProfile.js"),
);
// 1. Finance Related Lazy Components
const StaffPayroll = lazy(
  () => import("../Modules/Admin/staffPayroll/StaffPayroll.js"),
);
const RecentQuotationList = lazy(
  () => import("../Modules/Admin/Finance/Quotations/RecentQuotationList.js"),
);
const EntityRevenueDash = lazy(
  () => import("../Modules/Admin/Finance/entityRevenue/EntityRevenueDash.js"),
);
const AddEntityRevenue = lazy(
  () => import("../Modules/Admin/Finance/entityRevenue/AddEntityRevenue.js"),
);
const EntityRevenueList = lazy(
  () => import("../Modules/Admin/Finance/entityRevenue/EntityRevenueList.js"),
);
const BudgetPlannerDash = lazy(
  () => import("../Modules/Admin/Finance/budget/BudgetDash.js"),
);
const BudgetPlannerList = lazy(
  () => import("../Modules/Admin/Finance/budget/BudgetList.js"),
);
const BankReconciliationList = lazy(
  () => import("../Modules/Admin/Finance/bankRecon/BankReconciliationList.js"),
);
const BankReconciliationDash = lazy(
  () => import("../Modules/Admin/Finance/bankRecon/BankReconciliation.js"),
);
const StartReconciliation = lazy(
  () => import("../Modules/Admin/Finance/bankRecon/StartReconciliation.js"),
);
const DayBook = lazy(
  () => import("../Modules/Admin/Finance/dayBook/DayBookMain.js"),
);
const ConfigurationMain = lazy(
  () => import("../Modules/Admin/Finance/Configuration/ConfigurationMain.js"),
);
const PayRollDash = lazy(
  () => import("../Modules/Admin/Finance/Payroll/PayRollDash.js"),
);
const AddPayRollDash = lazy(
  () => import("../Modules/Admin/Finance/Payroll/AddPayRoll.js"),
);
const PayRollList = lazy(
  () => import("../Modules/Admin/Finance/Payroll/PayrollList.js"),
);
const OperationalExpensesDash = lazy(
  () => import("../Modules/Admin/Finance/operational-expenses/ExpsenseDash.js"),
);
const AddOperationalExpenses = lazy(
  () => import("../Modules/Admin/Finance/operational-expenses/AddExpsense.js"),
);
const ExpenseList = lazy(
  () => import("../Modules/Admin/Finance/operational-expenses/ExpenseList.js"),
);
const FinanceCategory = lazy(
  () => import("../Modules/Admin/Finance/financeCategory/FinanceCategory.js"),
);
const Inventory = lazy(
  () => import("../Modules/Admin/Finance/Inventory/Inventory.js"),
);
const LowInventory = lazy(
  () => import("../Modules/Admin/Finance/Inventory/LowStockList.js"),
);
const Entities = lazy(
  () => import("../Modules/Admin/Finance/entities/Enities.js"),
);

// 2. Transportation Related
const RouteAndBus = lazy(
  () => import("../Modules/Admin/Transportation/RouteAndBus"),
);
const RouteManagement = lazy(
  () => import("../Modules/Admin/Transportation/RouteManagement.js"),
);
const StudentStaffTransportation = lazy(
  () => import("../Modules/Admin/Transportation/StudentStaffTransportation.js"),
);
const DriverStaffTransportation = lazy(
  () => import("../Modules/Admin/Transportation/DriverStaffTransportation.js"),
);
const Maintenance = lazy(
  () => import("../Modules/Admin/Transportation/Maintenance.js"),
);
const RFID = lazy(() => import("../Modules/Admin/Transportation/RFID.js"));
const VehicleManagement = lazy(
  () => import("../Modules/Admin/Transportation/VehicleManagement.js"),
);
const ShiftManagement = lazy(
  () => import("../Modules/Admin/Transportation/ShiftManagement.js"),
);
const DriverVehicleAssignmentPage = lazy(
  () =>
    import("../Modules/Admin/Transportation/DriverVehicleAssignmentPage.js"),
);
const Stoppage = lazy(
  () => import("../Components/Transportation/StopageList.js"),
);

const Scheduling = lazy(
  () => import("../Modules/Admin/Transportation/Scheduling.js"),
);

const ViewTripLogs = lazy(
  () => import("../Modules/Admin/Transportation/ViewTripLogs.js"),
);
const ViewTrip = lazy(
  () =>
    import("../Modules/Admin/Transportation/RouteManagement/Components/ViewTrip/ViewTrip.js"),
);

const LiveTracking = lazy(
  () => import("../Modules/Admin/Transportation/LiveTracking.js"),
);

// Project Management
const ProjectManagementMain = lazy(
  () => import("../Modules/Admin/ProjectManagement/ProjectManagementMain.js"),
);
const CreateProject = lazy(
  () => import("../Modules/Admin/ProjectManagement/CreateProject.js"),
);
const KanbanBoard = lazy(
  () => import("../Modules/Admin/ProjectManagement/KanbanBoard.js"),
);
const AddTask = lazy(
  () => import("../Modules/Admin/ProjectManagement/AddTask.js"),
);
const ProjectView = lazy(
  () => import("../Modules/Admin/ProjectManagement/ProjectView.js"),
);
const TaskView = lazy(
  () => import("../Modules/Admin/ProjectManagement/TaskView.js"),
);

// 3. Academic Management
const Academic = lazy(
  () => import("../Modules/Admin/AcademicYear/Academic.js"),
);
const FinancialYear = lazy(
  () => import("../Modules/Admin/FinancialYear/FinanceYear.js"),
);
const SelectBranch = lazy(
  () => import("../Modules/LoginPages/Staff/Admin/SelectBranch.js"),
);
const Branch = lazy(() => import("../Modules/Admin/Branchs/Branch.js"));
const commonAcademic = lazy(
  () => import("../Components/Common/AcademicYear/Academic.js"),
);
const CreateAcademicYear = lazy(
  () => import("../Components/Admin/CreateAcademicYear.js"),
);

// 5. Subject and Curriculum Management
const ViewPage = lazy(
  () => import("../Modules/Admin/Subjects/Modules/Pages/ViewPage/ViewPage.js"),
);
const AddPage = lazy(
  () => import("../Modules/Admin/Subjects/Modules/Pages/AddPage/AddPage.js"),
);
const Page = lazy(
  () => import("../Modules/Admin/Subjects/Modules/Pages/Page.js"),
);

const DiscussionView = lazy(
  () =>
    import("../Modules/Admin/Subjects/Modules/Discussion/DiscussionView/DiscussionView.js"),
);

const AddDiscussion = lazy(
  () =>
    import("../Modules/Admin/Subjects/Modules/Discussion/AddDiscussion/AddDiscussion.js"),
);
const Discussion = lazy(
  () => import("../Modules/Admin/Subjects/Modules/Discussion/Discussion.js"),
);

const ApplicationRequests = lazy(
  () => import("../Modules/Admin/ApplicationRequests/ApplicationRequests.js"),
);

const Classes = lazy(() => import("../Modules/Admin/Classes/Classes.js"));
const Class = lazy(() => import("../Modules/Admin/Classes/SubClass/Class.js"));

// 10. Admin Dashboard and Miscellaneous
const Dash = lazy(() => import("../Modules/Admin/Dashboard/Dash.js"));

const EventSchool = lazy(
  () =>
    import("../Modules/Admin/NoticeBoard/Events/MainSection/EventSchool.js"),
);
const AdminNotice = lazy(
  () => import("../Modules/Admin/NoticeBoard/Notice/AdminNotice.js"),
);
const RoleSelector = lazy(
  () => import("../Components/Common/RBAC/RoleSelector.js"),
);
const StaffApplication = lazy(
  () => import("../Modules/Admin/Applications/MainSection.jsx"),
);

const Penalties = lazy(
  () => import("../Modules/Admin/Finance/Penalties/MainSection.js"),
);

const CertificateAndLetterManagement = lazy(
  () =>
    import("../Modules/Admin/certificateAndLetterManagement/CertificateAndLetterManagement.js"),
);
const ManageTemplates = lazy(
  () =>
    import("../Modules/Admin/certificateAndLetterManagement/components/ManageTemplates.jsx"),
);
const AddTemplate = lazy(
  () =>
    import("../Modules/Admin/certificateAndLetterManagement/components/AddTemplate.jsx"),
);
const GenerateDocument = lazy(
  () =>
    import("../Modules/Admin/certificateAndLetterManagement/components/GenerateDocument.jsx"),
);
const DigitalSignature = lazy(
  () =>
    import("../Modules/Admin/certificateAndLetterManagement/components/DigitalSignature.jsx"),
);
const ManageStamps = lazy(
  () =>
    import("../Modules/Admin/certificateAndLetterManagement/components/ManageStamps.jsx"),
);
const DocumentManagement = lazy(
  () => import("../Modules/Admin/DocumentsManagement/DocumentManagement.js"),
);
function App() {
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  useFirebaseMessaging();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const AppRouter = createBrowserRouter([
    // common-------------------------------------------------------------------------------------
    { path: "/", element: <Home />, errorElement: <Error /> },
    {
      path: "/dashboard/select/academic",
      element: (
        <ProtectRoute
          Component={commonAcademic}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    { path: "/stafflogin", element: <StaffLogin />, errorElement: <Error /> },
    {
      path: "/transport/sim",
      element: <TransportSimulator />,
      errorElement: <Error />,
    }, // sim

    {
      path: "/privacy-policy",
      element: <PrivacyPolicy />,
      errorElement: <Error />,
    },
    {
      path: "/terms-and-conditions",
      element: <TermsAndConditions />,
      errorElement: <Error />,
    },
    {
      path: "/cookie-policy",
      element: <CookiePolicy />,
      errorElement: <Error />,
    },

    {
      path: "/reset_password/:token",
      element: <ResetPassword />,
      errorElement: <Error />,
    },
    {
      path: "/forget_password",
      element: <ForgetPassword />,
      errorElement: <Error />,
    },
    {
      path: "/create_academicYear",
      element: (
        <ProtectRoute Component={CreateAcademicYear} allowedRoles={["admin"]} />
      ),
      errorElement: <Error />,
    },

    {
      path: "/select_branch",
      element: (
        <ProtectRoute Component={SelectBranch} allowedRoles={["admin"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/select_role",
      element: (
        <ProtectRoute
          Component={RoleSelector}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },

    {
      path: "/dashboard/academic",
      element: (
        <ProtectRoute Component={Academic} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/financialYear",
      element: (
        <ProtectRoute Component={FinancialYear} allowedRoles={["admin"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/categories",
      element: (
        <ProtectRoute
          Component={FinanceCategory}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/Inventory",
      element: (
        <ProtectRoute Component={Inventory} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/lowStock/Inventory",
      element: (
        <ProtectRoute
          Component={LowInventory}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/Inventory/list",
      element: (
        <ProtectRoute
          Component={InventoryList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/entities",
      element: (
        <ProtectRoute Component={Entities} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/penalties",
      element: (
        <ProtectRoute Component={Penalties} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/overview",
      element: (
        <ProtectRoute
          Component={OverviewMain}
          allowedRoles={["admin", "finance", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/investment",
      element: (
        <ProtectRoute
          Component={InvestmentMain}
          allowedRoles={["admin", "staff", "finance"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/liabilities",
      element: (
        <ProtectRoute
          Component={LiabilitiesMain}
          allowedRoles={["admin", "staff", "finance"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/statements",
      element: (
        <ProtectRoute
          Component={FinancialStatements}
          allowedRoles={["admin", "staff", "finance"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/assets",
      element: (
        <ProtectRoute
          Component={FixedAssets}
          allowedRoles={["admin", "staff", "finance"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/vouchers",
      element: (
        <ProtectRoute
          Component={CashFlow}
          allowedRoles={["admin", "staff", "finance"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/dashboard/all/branch",
      element: (
        <ProtectRoute Component={Branch} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },

    //Admin--------------------------------------------------------------------------
    {
      path: "/dashboard",
      element: (
        <ProtectRoute Component={Dash} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },

    {
      path: "/class",
      element: (
        <ProtectRoute Component={Classes} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/application_staff",
      element: (
        <ProtectRoute Component={StaffApplication} allowedRoles={["staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid",
      element: (
        <ProtectRoute Component={Class} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },

    {
      path: "/application-requests",
      element: (
        <ProtectRoute
          Component={ApplicationRequests}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },

    {
      path: "/certificates&lettermanagement",
      element: (
        <ProtectRoute
          Component={CertificateAndLetterManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/certificates&lettermanagement/managetemplates",
      element: (
        <ProtectRoute
          Component={ManageTemplates}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/certificates&lettermanagement/managetemplates/add-template",
      element: (
        <ProtectRoute
          Component={AddTemplate}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/certificates&lettermanagement/generate-document",
      element: (
        <ProtectRoute
          Component={GenerateDocument}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/certificates&lettermanagement/digital-signatures",
      element: (
        <ProtectRoute
          Component={DigitalSignature}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/certificates&lettermanagement/manage-stamps",
      element: (
        <ProtectRoute
          Component={ManageStamps}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/document-management",
      element: (
        <ProtectRoute
          Component={DocumentManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/routes-and-buses",
      element: (
        <ProtectRoute
          Component={RouteAndBus}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/vehicle-management",
      element: (
        <ProtectRoute
          Component={VehicleManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/driver-vehicle-assignment",
      element: (
        <ProtectRoute
          Component={DriverVehicleAssignmentPage}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/route-management",
      element: (
        <ProtectRoute
          Component={RouteManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/shift-management",
      element: (
        <ProtectRoute
          Component={ShiftManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/schedule",
      element: (
        <ProtectRoute
          Component={Scheduling}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/student-staff",
      element: (
        <ProtectRoute
          Component={StudentStaffTransportation}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/driver-staff",
      element: (
        <ProtectRoute
          Component={DriverStaffTransportation}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/maintenance",
      element: (
        <ProtectRoute
          Component={Maintenance}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/rfid",
      element: (
        <ProtectRoute Component={RFID} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/route-management/routes/:id/stoppages",
      element: (
        <ProtectRoute Component={Stoppage} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/route-management/view-trip/:vehicleId/logs",
      element: (
        <ProtectRoute
          Component={ViewTripLogs}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/route-management/trio/:tripId/view",
      element: (
        <ProtectRoute Component={ViewTrip} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/transportation/live-tracking",
      element: (
        <ProtectRoute
          Component={LiveTracking}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    // Project Management Routes
    {
      path: "/project-management/tasks",
      element: (
        <ProtectRoute
          Component={ProjectManagementMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/project-management/create-project",
      element: (
        <ProtectRoute
          Component={CreateProject}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/project-management/task-management/:projectId",
      element: (
        <ProtectRoute
          Component={KanbanBoard}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/project-management/add-task/:projectId",
      element: (
        <ProtectRoute Component={AddTask} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/project-management/project/:projectId",
      element: (
        <ProtectRoute
          Component={ProjectView}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/project-management/:projectId/task/:taskId",
      element: (
        <ProtectRoute Component={TaskView} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid/:sid/discussions",
      element: (
        <ProtectRoute
          Component={Discussion}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid/:sid/create_discussion",
      element: (
        <ProtectRoute
          Component={AddDiscussion}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid/:sid/discussions/:did/view",
      element: (
        <ProtectRoute
          Component={DiscussionView}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid/:sid/page",
      element: (
        <ProtectRoute Component={Page} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid/:sid/page/create_page",
      element: (
        <ProtectRoute Component={AddPage} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/class/:cid/:sid/page/:pid/view",
      element: (
        <ProtectRoute Component={ViewPage} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },

    {
      path: "/Staff-Attendance",
      element: (
        <ProtectRoute
          Component={StaffAttendance}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/Staff-Individual-Attendance/:sid",
      element: (
        <ProtectRoute
          Component={IndividualStaffAttendance}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/staff/take_attendance",
      element: (
        <ProtectRoute
          Component={TakeStaffAttendance}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },

    {
      path: "/noticeboard/events",
      element: (
        <ProtectRoute
          Component={EventSchool}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/noticeboard/notice",
      element: (
        <ProtectRoute
          Component={AdminNotice}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/user-payroll",
      element: (
        <ProtectRoute
          Component={StaffPayroll}
          allowedRoles={["teacher", "staff"]}
        />
      ),
      errorElement: <Error />,
    },

    {
      path: "/finance/entity/revenue/add",
      element: (
        <ProtectRoute
          Component={AddEntityRevenue}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/budget-planner",
      element: (
        <ProtectRoute
          Component={BudgetPlannerDash}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/budget-planner/list",
      element: (
        <ProtectRoute
          Component={BudgetPlannerList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/bank-reconciliation",
      element: (
        <ProtectRoute
          Component={BankReconciliationDash}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/bank-reconciliation/list",
      element: (
        <ProtectRoute
          Component={BankReconciliationList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/bank-reconciliation/start-reconciliation",
      element: (
        <ProtectRoute
          Component={StartReconciliation}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/day-book",
      element: (
        <ProtectRoute Component={DayBook} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/configuration",
      element: (
        <ProtectRoute
          Component={ConfigurationMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/payroll",
      element: (
        <ProtectRoute
          Component={PayRollDash}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/payroll/add",
      element: (
        <ProtectRoute
          Component={AddPayRollDash}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/payroll/list",
      element: (
        <ProtectRoute
          Component={PayRollList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/operational-expenses",
      element: (
        <ProtectRoute
          Component={OperationalExpensesDash}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/operational-expenses/add",
      element: (
        <ProtectRoute
          Component={AddOperationalExpenses}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/operational-expenses/list",
      element: (
        <ProtectRoute
          Component={ExpenseList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/entity/revenue/list",
      element: (
        <ProtectRoute
          Component={EntityRevenueList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },

    {
      path: "/finance/entity/revenue",
      element: (
        <ProtectRoute
          Component={EntityRevenueDash}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/receipts",
      element: (
        <ProtectRoute
          Component={ReceiptsMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/Payroll-Management",
      element: (
        <ProtectRoute
          Component={PayrollManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/AddPayrollManagement",
      element: (
        <ProtectRoute
          Component={AddPayrollManagement}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/receipts/receipt-list",
      element: (
        <ProtectRoute
          Component={RecentReceiptsList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/receipts/add-new-receipt",
      element: (
        <ProtectRoute
          Component={CreateReceipt}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/quotations",
      element: (
        <ProtectRoute
          Component={QuotationMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/quotations/add-new-quotations",
      element: (
        <ProtectRoute
          Component={CreateQuotation}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/finance/quotations/quotations-list",
      element: (
        <ProtectRoute
          Component={RecentQuotationList}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },

    {
      path: "/users/manage-roles",
      element: (
        <ProtectRoute
          Component={ManageRolePage}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/users/integrate-careers",
      element: (
        <ProtectRoute
          Component={IntegrateCarrersDocs}
          allowedRoles={["admin"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/users/helper",
      element: <AllHelper />,
      errorElement: <Error />,
    },
    {
      path: "/users/driver",
      element: <AllDriver />,
      errorElement: <Error />,
    },
    { path: "/users/staffs", element: <AllStaff />, errorElement: <Error /> },
    {
      path: "/users/admin",
      element: (
        <ProtectRoute Component={UserProfile} allowedRoles={["admin"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/users/my/profile",
      element: (
        <ProtectRoute Component={StaffMyProfile} allowedRoles={["staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/benifit-compensation",
      element: (
        <ProtectRoute
          Component={BenifitMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/add-benifit",
      element: (
        <ProtectRoute
          Component={AddBenifit}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/manager-approvals",
      element: (
        <ProtectRoute
          Component={ManagerApproval}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/visitor-management",
      element: (
        <ProtectRoute
          Component={VisitorMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/direct-visitor",
      element: (
        <ProtectRoute
          Component={DirectVisitor}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/preregistered-visitor",
      element: (
        <ProtectRoute
          Component={AddPreVisitor}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/careers",
      element: (
        <ProtectRoute Component={Main} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/careers/form-builder",
      element: (
        <ProtectRoute
          Component={DynamicForm}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "/careers/create",
      element: (
        <ProtectRoute Component={CreateJob} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/careers/edit/:id",
      element: (
        <ProtectRoute Component={CreateJob} allowedRoles={["admin", "staff"]} />
      ),
      errorElement: <Error />,
    },
    {
      path: "/careers/applications/:jobId",
      element: (
        <ProtectRoute
          Component={JobApplicationsPage}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "Interviews",
      element: (
        <ProtectRoute
          Component={Interviews}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
    {
      path: "interview/:jobId/:applicationId/:roundId",
      element: (
        <ProtectRoute
          Component={ScheduleInterviewMain}
          allowedRoles={["admin", "staff"]}
        />
      ),
      errorElement: <Error />,
    },
  ]);

  return (
    <>
      {!isOnline && <Offline />}
      <Suspense fallback={<Fallback />}>
        <RouterProvider router={AppRouter} />
      </Suspense>
    </>
  );
}

export default App;
